// client/src/contexts/AppDataContext.js
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuth } from "./AuthContext";
import { getFolders, getUserReviewers, getStudentStats, getStudyGroups } from "../services/api";
import { computeVerified } from "../utils/libraryUtils";
import { queryCache } from "../lib/queryCache";

const AppDataContext = createContext(null);

const CACHE_KEYS = {
  FOLDERS: "library_folders",
  RECENT_NOTES: "library_recent_notes",
  STATS: "student_stats",
  STUDY_GROUPS: "study_groups_list",
};

const EMPTY_ARRAY = [];

const DEFAULT_STATS = {
  totalSeconds: 0,
  streak: 0,
  quizHours: 0,
  readingHours: 0,
  flashcardHours: 0,
};

const enrichVerified = (notes) => {
  if (!Array.isArray(notes) || notes.length === 0) return EMPTY_ARRAY;
  return notes.map((n) => ({ ...n, verified: computeVerified(n) }));
};

export const AppDataProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const prevUserRef = useRef(user);

  // Cached state with cache-initializers
  const [folders, setFolders] = useState(() => queryCache.get(CACHE_KEYS.FOLDERS) || EMPTY_ARRAY);
  const [recentNotes, setRecentNotes] = useState(() => queryCache.get(CACHE_KEYS.RECENT_NOTES) || EMPTY_ARRAY);
  const [libraryLoading, setLibraryLoading] = useState(false);

  const [stats, setStats] = useState(() => queryCache.get(CACHE_KEYS.STATS) || DEFAULT_STATS);
  const [statsLoading, setStatsLoading] = useState(false);

  const [studyGroups, setStudyGroups] = useState(() => queryCache.get(CACHE_KEYS.STUDY_GROUPS) || null);
  const [studyGroupsLoading, setStudyGroupsLoading] = useState(false);

  // Sync to cache on external state updates (e.g. user renames folder, edits reviewer in UI)
  const updateFolders = useCallback((updater) => {
    setFolders((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      queryCache.set(CACHE_KEYS.FOLDERS, next);
      return next;
    });
  }, []);

  const updateRecentNotes = useCallback((updater) => {
    setRecentNotes((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      queryCache.set(CACHE_KEYS.RECENT_NOTES, next);
      return next;
    });
  }, []);

  const updateStats = useCallback((updater) => {
    setStats((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      queryCache.set(CACHE_KEYS.STATS, next);
      return next;
    });
  }, []);

  const updateStudyGroups = useCallback((updater) => {
    setStudyGroups((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      queryCache.set(CACHE_KEYS.STUDY_GROUPS, next);
      return next;
    });
  }, []);

  // --- FETCHERS ---
  const fetchLibraryData = useCallback(async (force = false) => {
    if (!user) return;

    if (!force) {
      const cachedFolders = queryCache.get(CACHE_KEYS.FOLDERS);
      const cachedNotes = queryCache.get(CACHE_KEYS.RECENT_NOTES);
      if (cachedFolders && cachedNotes) {
        setFolders(cachedFolders);
        setRecentNotes(cachedNotes);
        setLibraryLoading(false);
        return;
      }
    }

    setLibraryLoading(true);
    try {
      const [folderResult, filesResult] = await Promise.allSettled([
        getFolders(),
        getUserReviewers(),
      ]);

      if (folderResult.status === "fulfilled") {
        const p = folderResult.value;
        const newFolders = Array.isArray(p) ? p : p?.folders || p?.data || EMPTY_ARRAY;
        setFolders(newFolders);
        queryCache.set(CACHE_KEYS.FOLDERS, newFolders, 5 * 60 * 1000);
      }

      if (filesResult.status === "fulfilled") {
        const p = filesResult.value;
        const rawFiles = Array.isArray(p) ? p : p?.data || p?.reviewers || p?.files || EMPTY_ARRAY;
        rawFiles.sort((a, b) => {
          const dA = new Date(a.lastReviewedAt || a.date || a.createdAt || 0);
          const dB = new Date(b.lastReviewedAt || b.date || b.createdAt || 0);
          return dB - dA;
        });
        const enriched = enrichVerified(rawFiles);
        setRecentNotes(enriched);
        queryCache.set(CACHE_KEYS.RECENT_NOTES, enriched, 5 * 60 * 1000);
      }
    } catch (err) {
      console.error("[AppDataContext] Error loading library data:", err);
    } finally {
      setLibraryLoading(false);
    }
  }, [user]);

  const fetchStats = useCallback(async (force = false) => {
    if (!user) return;

    if (!force) {
      const cachedStats = queryCache.get(CACHE_KEYS.STATS);
      if (cachedStats) {
        setStats(cachedStats);
        setStatsLoading(false);
        return;
      }
    }

    setStatsLoading(true);
    try {
      const res = await getStudentStats();
      const data = res?.data || res;
      if (data) {
        const getHours = (type) => {
          const item = data.breakdown?.find((b) => b.activity === type);
          return item ? item.hours : 0;
        };
        const readTime = (data.readingTotalHours || 0) + getHours("note-taking");

        const parsedStats = {
          totalSeconds: data.totalSeconds || 0,
          streak: data.streak || 0,
          quizHours: data.quizTotalHours || getHours("quiz") || getHours("exam") || 0,
          readingHours: readTime,
          flashcardHours: data.flashcardTotalHours || getHours("flashcards") || 0,
        };
        setStats(parsedStats);
        queryCache.set(CACHE_KEYS.STATS, parsedStats, 5 * 60 * 1000);
      }
    } catch (err) {
      console.error("[AppDataContext] Error loading stats:", err);
    } finally {
      setStatsLoading(false);
    }
  }, [user]);

  const fetchStudyGroups = useCallback(async (force = false) => {
    if (!user) return null;

    if (!force) {
      const cachedGroups = queryCache.get(CACHE_KEYS.STUDY_GROUPS);
      if (cachedGroups) {
        setStudyGroups(cachedGroups);
        return cachedGroups;
      }
    }

    setStudyGroupsLoading(true);
    try {
      const data = await getStudyGroups();
      const list = Array.isArray(data) ? data : EMPTY_ARRAY;
      setStudyGroups(list);
      queryCache.set(CACHE_KEYS.STUDY_GROUPS, list, 5 * 60 * 1000);
      return list;
    } catch (err) {
      console.error("[AppDataContext] Error loading study groups:", err);
      return null;
    } finally {
      setStudyGroupsLoading(false);
    }
  }, [user]);

  // Initial load when user changes or authenticates
  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      if (prevUserRef.current) {
        // Reset only on logout transition
        setFolders(EMPTY_ARRAY);
        setRecentNotes(EMPTY_ARRAY);
        setStats(DEFAULT_STATS);
        setStudyGroups(null);
        setLibraryLoading(false);
        setStatsLoading(false);
      }
      prevUserRef.current = null;
      return;
    }

    // User is logged in
    prevUserRef.current = user;
    fetchLibraryData(false);
    fetchStats(false);
  }, [user, authLoading, fetchLibraryData, fetchStats]);

  const addFolderToState = useCallback((newFolder) => {
    updateFolders((prev) => [...prev, newFolder]);
  }, [updateFolders]);

  const markNoteAccessed = useCallback((noteId) => {
    if (!noteId) return;
    const now = new Date().toISOString();
    updateRecentNotes((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      const targetIndex = prev.findIndex((n) => String(n._id || n.id) === String(noteId));
      if (targetIndex === -1) return prev;
      const target = { ...prev[targetIndex], lastReviewedAt: now };
      const next = [target, ...prev.filter((_, idx) => idx !== targetIndex)];
      return next;
    });
  }, [updateRecentNotes]);

  const refresh = useCallback((key) => {
    switch (key) {
      case "library":
      case "folders":
      case "reviewers":
        queryCache.invalidate(CACHE_KEYS.FOLDERS);
        queryCache.invalidate(CACHE_KEYS.RECENT_NOTES);
        return fetchLibraryData(true);
      case "stats":
        queryCache.invalidate(CACHE_KEYS.STATS);
        return fetchStats(true);
      case "studyGroups":
        queryCache.invalidate(CACHE_KEYS.STUDY_GROUPS);
        return fetchStudyGroups(true);
      case "all":
      default:
        queryCache.invalidate(CACHE_KEYS.FOLDERS);
        queryCache.invalidate(CACHE_KEYS.RECENT_NOTES);
        queryCache.invalidate(CACHE_KEYS.STATS);
        queryCache.invalidate(CACHE_KEYS.STUDY_GROUPS);
        return Promise.allSettled([
          fetchLibraryData(true),
          fetchStats(true),
          fetchStudyGroups(true),
        ]);
    }
  }, [fetchLibraryData, fetchStats, fetchStudyGroups]);

  const invalidate = useCallback((key) => {
    if (key === "reviewers" || key === "recentNotes") {
      queryCache.invalidate(CACHE_KEYS.RECENT_NOTES);
    } else if (key === "folders") {
      queryCache.invalidate(CACHE_KEYS.FOLDERS);
    } else if (key === "stats") {
      queryCache.invalidate(CACHE_KEYS.STATS);
    } else if (key === "studyGroups") {
      queryCache.invalidate(CACHE_KEYS.STUDY_GROUPS);
    } else if (key) {
      queryCache.invalidate(key);
    }
  }, []);

  const refreshLibrary = useCallback(() => refresh("library"), [refresh]);
  const refreshStats = useCallback(() => refresh("stats"), [refresh]);
  const refreshStudyGroups = useCallback(() => refresh("studyGroups"), [refresh]);

  const value = useMemo(
    () => ({
      folders,
      setFolders: updateFolders,
      recentNotes,
      setRecentNotes: updateRecentNotes,
      libraryLoading,
      stats,
      setStats: updateStats,
      statsLoading,
      studyGroups,
      setStudyGroups: updateStudyGroups,
      studyGroupsLoading,
      fetchStudyGroups,
      refreshLibrary,
      refreshStats,
      refreshStudyGroups,
      refresh,
      invalidate,
      addFolderToState,
      markNoteAccessed,
    }),
    [
      folders,
      updateFolders,
      recentNotes,
      updateRecentNotes,
      libraryLoading,
      stats,
      updateStats,
      statsLoading,
      studyGroups,
      updateStudyGroups,
      studyGroupsLoading,
      fetchStudyGroups,
      refreshLibrary,
      refreshStats,
      refreshStudyGroups,
      refresh,
      invalidate,
      addFolderToState,
      markNoteAccessed,
    ]
  );

  return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>;
};

export const useAppData = () => {
  const context = useContext(AppDataContext);
  if (!context) {
    throw new Error("useAppData must be used within an AppDataProvider");
  }
  return context;
};

export default AppDataContext;
